import type { RoofAggregates, RoofvizPacket } from "./contract.ts";
import type { GutterSpec } from "./gutters.ts";
import type { FasciaSpec } from "./fascia.ts";
import type { Profile } from "./materials.ts";
import { calculateRelativePrice } from "./materials.ts";
import type { RoofInsightId } from "./roofInsights.ts";

export type StoryView = "north" | "east" | "south" | "west";

export interface RoofStoryChapter {
  id: "mapped" | "shape" | "junctions" | "price" | "finish" | "next";
  title: string;
  body: string;
  value: string;
  points: string[];
  focus: RoofInsightId;
  view?: StoryView;
  blueprint?: boolean;
}

export interface CostFactor {
  id: "material" | "roof_form" | "access" | "existing_roof";
  label: string;
  value: string;
  status: "updates" | "mapped" | "confirm";
  explanation: string;
}

export type RoofAdditionId = "solar" | "skylight" | "gutters";

export interface RoofAddition {
  id: RoofAdditionId;
  name: string;
  label: string;
  summary: string;
  facts: string[];
  disclosure: string;
}

function metres(value: number): string {
  return `${value.toFixed(1)} m`;
}

function pitchSummary(packet: RoofvizPacket): string {
  const pitches = [...new Set(packet.planes.map((plane) => plane.pitchDeg))];
  if (pitches.length === 1) return `${pitches[0].toFixed(1)}°`;
  return `${Math.min(...pitches).toFixed(1)}–${Math.max(...pitches).toFixed(1)}°`;
}

export function buildRoofStory(
  packet: RoofvizPacket,
  profile: Profile,
  gutter: GutterSpec,
  referenceProfile: Profile,
  fascia?: FasciaSpec,
): RoofStoryChapter[] {
  const junctionM = packet.aggregates.ridgeM + packet.aggregates.hipM + packet.aggregates.valleyM;
  const relative = calculateRelativePrice(packet.aggregates, profile, referenceProfile);
  return [
    {
      id: "mapped",
      title: "We mapped your roof",
      body: "The plan, roof planes and important junctions combine to create the digital roof you can explore.",
      value: `${packet.aggregates.surfaceAreaM2.toFixed(0)} m² mapped roof`,
      points: [
        `${packet.planes.length} connected roof planes`,
        `${pitchSummary(packet)} supplied roof pitch`,
        "Roof height remains visibly labelled where it is assumed",
      ],
      focus: "roof",
      view: "north",
      blueprint: true,
    },
    {
      id: "shape",
      title: "Its shape guides the installation",
      body: "Long-run sheets follow the fall of each plane from the high roof lines towards the eaves and gutters.",
      value: `${pitchSummary(packet)} roof pitch`,
      points: [
        `${metres(packet.aggregates.eaveM)} of mapped eaves`,
        "Profile direction follows each roof plane",
        "Final set-out and sheet lengths are confirmed by the roofing team",
      ],
      focus: "pitch",
      view: "east",
    },
    {
      id: "junctions",
      title: "The finishing details matter",
      body: "Ridges, hips and valleys join the roof planes and are finished with profile-appropriate folded metalwork.",
      value: `${metres(junctionM)} of junctions`,
      points: [
        `${metres(packet.aggregates.ridgeM)} ridge`,
        `${metres(packet.aggregates.hipM)} hips`,
        `${metres(packet.aggregates.valleyM)} valley`,
      ],
      focus: "junctions",
      view: "south",
    },
    {
      id: "price",
      title: "The roof edge completes the look",
      body: "Gutter, fascia, eave closures and drip edges turn the roof sheets into one complete installed appearance.",
      value: gutter.name,
      points: [
        `${fascia?.name ?? "The selected fascia"} finishes the edge`,
        "Connected gutter corners do not receive ordinary stop ends",
        "Outlets, fall and downpipes are confirmed for the real job",
      ],
      focus: "rainwater",
      view: "west",
    },
    {
      id: "finish",
      title: "Now choose the material character",
      body: "The selected Roofing Industries profile changes the roof's rhythm, fixing appearance and relative material effect—not its mapped shape.",
      value: `${profile.name} · ${relative.label}`,
      points: [
        profile.installedLook.sheetDirection,
        profile.installedLook.fixing,
        "Screen colour is indicative; a physical sample confirms the final choice",
      ],
      focus: "roof",
      view: "north",
    },
    {
      id: "next",
      title: "Take the next useful step",
      body: "Save what you like, prepare a physical sample request, or ask the roofing team to assess an addition.",
      value: "Your roof, your decision",
      points: [
        "Physical samples confirm colour and finish",
        "Solar and skylights begin with a professional assessment",
        "Nothing in this standalone view updates the job",
      ],
      focus: "roof",
    },
  ];
}

export function buildCostFactors(
  aggregates: RoofAggregates,
  profile: Profile,
  referenceProfile: Profile,
): CostFactor[] {
  const relative = calculateRelativePrice(aggregates, profile, referenceProfile);
  const junctionM = aggregates.ridgeM + aggregates.hipM + aggregates.valleyM;
  return [
    {
      id: "material",
      label: "Material",
      value: relative.label,
      status: "updates",
      explanation: "This comparison updates as you change the Roofing Industries profile.",
    },
    {
      id: "roof_form",
      label: "Roof form",
      value: `${aggregates.surfaceAreaM2.toFixed(0)} m² · ${metres(junctionM)} junctions`,
      status: "mapped",
      explanation: "Mapped roof aggregates explain the scale and finishing detail without exposing a takeoff schedule.",
    },
    {
      id: "access",
      label: "Access & labour",
      value: `${aggregates.storeys} storey · site check required`,
      status: "confirm",
      explanation: "Scaffolding, delivery, working access and programme are confirmed for the property.",
    },
    {
      id: "existing_roof",
      label: "Existing roof",
      value: "To be confirmed",
      status: "confirm",
      explanation: "Removal, disposal, substrate condition and any repairs are not inferred by the visualizer.",
    },
  ];
}

export function buildRoofAdditions(packet: RoofvizPacket, gutter: GutterSpec): RoofAddition[] {
  const mappedSolar = packet.objects.filter((object) => object.type === "solar_array").length;
  const mappedSkylights = packet.objects.filter((object) => object.type === "skylight").length;
  return [
    {
      id: "solar",
      name: "Solar assessment",
      label: "Explore potential",
      summary: "Use the mapped roof as the starting point for a solar specialist to assess suitable planes and system size.",
      facts: [
        `${pitchSummary(packet)} mapped roof pitch`,
        `${packet.aggregates.surfaceAreaM2.toFixed(0)} m² total roof surface`,
        mappedSolar > 0 ? `${mappedSolar} supplied solar object${mappedSolar === 1 ? "" : "s"}` : "No solar layout has been supplied",
      ],
      disclosure: "Panel count, shade, structure, wind, setbacks, fixing, wiring, yield and price require professional assessment.",
    },
    {
      id: "skylight",
      name: "Skylight assessment",
      label: "Explore daylight",
      summary: "Ask the roofing team to consider a rooflight location and a flashing detail suited to the roof profile and pitch.",
      facts: [
        mappedSkylights > 0 ? `${mappedSkylights} supplied skylight object${mappedSkylights === 1 ? "" : "s"}` : "No skylight location has been supplied",
        "Placement affects drainage and flashing",
        "Interior framing and light shaft are separate considerations",
      ],
      disclosure: "No skylight is added to this roof until its type, size, placement and installation detail are confirmed.",
    },
    {
      id: "gutters",
      name: "New gutters",
      label: "Include for review",
      summary: `${gutter.name} is shown as the current colour-matched visual option for the roof edge.`,
      facts: [
        `${metres(packet.aggregates.eaveM)} of mapped eaves`,
        gutter.fullCrossSectionMm2 === undefined
          ? "Published capacity to be checked"
          : `${gutter.fullCrossSectionMm2.toLocaleString("en-NZ")} mm² published full section`,
        "Outlets and downpipes are shown only when supplied with the mapped roof",
      ],
      disclosure: "Capacity, fall, outlets, downpipes, substrate, colour availability and price are confirmed for the job.",
    },
  ];
}
